import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import {
  devolverPrestamo,
  type InventarioRow,
  type PrestamoItem,
} from '../api';

/**
 * Close a loan: move units from Prest/Show back to Disponible and
 * flip the selected `22.Prestamos` row to Finalizado. Unlike Showroom
 * retornar, the prestamo selection is REQUIRED — a loan devolution
 * always closes one specific placement.
 */
export function DevolucionPrestamoDialog({
  row,
  activePrestamos,
  onClose,
}: {
  row: InventarioRow;
  activePrestamos: PrestamoItem[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [prestamoId, setPrestamoId] = useState<string>('');
  const [cantidadRaw, setCantidadRaw] = useState('');

  const cantidad = parseCantidad(cantidadRaw);
  const prestShow = row.buckets.prestShow;
  const exceeds = cantidad !== null && cantidad > prestShow + 0.0001;
  const canSubmit =
    cantidad !== null && cantidad > 0 && !exceeds && prestamoId !== '' && prestShow > 0;

  const mutation = useMutation({
    mutationFn: () =>
      devolverPrestamo(row.id, {
        cantidad: cantidad ?? 0,
        prestamoId,
      }),
    onSuccess: (r) => {
      if (
        r.outcome === 'insufficient_prestshow' ||
        r.outcome === 'prestamo_not_found' ||
        r.outcome === 'prestamo_already_closed'
      ) {
        toast.error(r.message);
        return;
      }
      toast.success(`${r.cantidad} un. devueltas`);
      qc.invalidateQueries({ queryKey: ['inventario'] });
      onClose();
    },
    onError: () => toast.error('No se pudo registrar la devolución'),
  });

  if (activePrestamos.length === 0) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="h-4 w-4" aria-hidden />
              Devolución de Préstamo
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              No hay préstamos activos para este artículo.
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Registrando devolución…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-4 w-4" aria-hidden />
            Devolución de Préstamo
          </DialogTitle>
          <DialogDescription>
            {row.fields.Articulo_I || 'artículo'} · Prest/Show actual:{' '}
            <strong>{prestShow}</strong> un.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
            <Bucket label="Prest/Show" value={prestShow} tone="rose" />
            <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
            <Bucket label="Disponible" value={row.buckets.disponible} tone="green" />
          </div>

          <div className="space-y-1">
            <Label htmlFor="dev-prestamo" className="text-sm">
              Préstamo a cerrar <span className="text-destructive">*</span>
            </Label>
            <Select value={prestamoId} onValueChange={setPrestamoId}>
              <SelectTrigger id="dev-prestamo">
                <SelectValue placeholder="Seleccionar préstamo…" />
              </SelectTrigger>
              <SelectContent>
                {activePrestamos.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.fields.Cliente_PPRES || '—'} · desde{' '}
                    {p.fields.FechaPrestamo_PPRES || '—'}
                    {p.fields.FechaPrestamoFin_PPRES
                      ? ` · vto ${p.fields.FechaPrestamoFin_PPRES}`
                      : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="dev-cantidad" className="text-sm">
              Cantidad a devolver <span className="text-destructive">*</span>
            </Label>
            <Input
              id="dev-cantidad"
              type="number"
              inputMode="decimal"
              min={0}
              max={prestShow}
              step="0.01"
              placeholder={`máx ${prestShow}`}
              value={cantidadRaw}
              onChange={(e) => setCantidadRaw(e.target.value)}
              className={cn('tabular-nums', exceeds && 'border-destructive')}
            />
            {exceeds && (
              <p className="text-xs text-destructive">
                Excede Prest/Show ({prestShow}).
              </p>
            )}
          </div>

          {cantidad !== null && cantidad > 0 && !exceeds && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Después: Prest/Show {prestShow - cantidad} · Disponible{' '}
              {row.buckets.disponible + cantidad}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="gap-2"
          >
            <Undo2 className="h-4 w-4" aria-hidden />
            Confirmar devolución
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bucket({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'rose' | 'green';
}) {
  const cls =
    tone === 'rose'
      ? 'bg-rose-100 text-rose-700'
      : 'bg-emerald-100 text-emerald-700';
  return (
    <div className="flex flex-1 flex-col items-center">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'mt-1 inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums',
          cls,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function parseCantidad(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}
